<template>
  <!-- Banner de actualización disponible -->
  <!-- Se muestra en la parte inferior de la pantalla, sin bloquear el reproductor -->
  <Transition name="pwa-banner">
    <div
      v-if="needsUpdate"
      class="pwa-update-banner"
      role="alert"
      aria-live="polite"
    >
      <div class="pwa-update-content">
        <!-- Ícono -->
        <div class="pwa-update-icon" aria-hidden="true">
          <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
            <path fill-rule="evenodd"
              d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
              clip-rule="evenodd"
            />
          </svg>
        </div>

        <!-- Texto -->
        <div class="pwa-update-text">
          <span class="pwa-update-title">Nueva versión disponible</span>
          <span class="pwa-update-subtitle">Al actualizar se recarga el reproductor y el audio se corta unos segundos. Elegí un momento sin salida al aire.</span>
        </div>

        <!-- Acciones -->
        <div class="pwa-update-actions">
          <button
            class="pwa-btn pwa-btn-primary"
            :class="{ 'pwa-btn-loading': isUpdating }"
            :disabled="isUpdating"
            @click="updateNow"
          >
            <span v-if="!isUpdating">Actualizar</span>
            <span v-else>Actualizando…</span>
          </button>
          <button
            class="pwa-btn pwa-btn-ghost"
            :disabled="isUpdating"
            @click="dismissUpdate"
          >
            Ahora no
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { usePwaUpdate } from '@/composables/usePwaUpdate'

const { needsUpdate, isUpdating, updateNow, dismissUpdate } = usePwaUpdate()
</script>

<style scoped>
.pwa-update-banner {
  position: fixed;
  bottom: 1.25rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  width: min(480px, calc(100vw - 2rem));
  background: var(--color-background-primary, #1e293b);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  padding: 0.875rem 1rem;
}

.pwa-update-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.pwa-update-icon {
  flex-shrink: 0;
  color: #60a5fa;
  display: flex;
  align-items: center;
}

.pwa-update-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.pwa-update-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text-primary, #f1f5f9);
  white-space: nowrap;
}

.pwa-update-subtitle {
  font-size: 0.75rem;
  color: var(--color-text-secondary, #94a3b8);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pwa-update-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.pwa-btn {
  border: none;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0.375rem 0.75rem;
  transition: opacity 0.15s, background 0.15s;
  white-space: nowrap;
}

.pwa-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.pwa-btn-primary {
  background: #3b82f6;
  color: #fff;
}

.pwa-btn-primary:hover:not(:disabled) {
  background: #2563eb;
}

.pwa-btn-primary.pwa-btn-loading {
  background: #1d4ed8;
}

.pwa-btn-ghost {
  background: transparent;
  color: var(--color-text-secondary, #94a3b8);
}

.pwa-btn-ghost:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.07);
  color: var(--color-text-primary, #f1f5f9);
}

/* Transición de entrada/salida */
.pwa-banner-enter-active,
.pwa-banner-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.pwa-banner-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(1rem);
}

.pwa-banner-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(0.5rem);
}
</style>
